import { useState, useEffect } from 'react';
import { fontData, getFontPath } from '../data/fonts';

export interface FontWeight {
  file: string;
  name: string;
  weight: string;
  value: string;
}

export interface FontFamily {
  family: string;
  name: string;
  weights: FontWeight[];
  language?: string; // 언어 정보 (예: '한글', '영문', '일본어', '중국어' 등)
}

export function useFontManager() {
  const [fontFamilies, setFontFamilies] = useState<FontFamily[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFonts = async () => {
      try {
        // fonts.ts에서 import한 폰트 데이터 사용
        const fontList: FontFamily[] = fontData;
        
        // 각 폰트 패밀리의 모든 두께를 FontFace API로 로드
        const loadedFamilies: FontFamily[] = [];
        
        for (const fontFamily of fontList) {
          const loadedWeights: FontWeight[] = [];
          
          for (const weightInfo of fontFamily.weights) {
            try {
              // 번들에 포함된 폰트 파일 경로 가져오기
              const fontPath = getFontPath(weightInfo.file);
              
              if (!fontPath) {
                console.warn(`폰트 파일 경로를 찾을 수 없습니다: ${weightInfo.file}`);
                loadedWeights.push(weightInfo);
                continue;
              }

              const fontDescriptor: FontFaceDescriptors = { weight: weightInfo.weight };
              
              // FontFace 생성 시 실제 폰트 패밀리 이름 사용
              // TTF 파일 내부의 실제 폰트 패밀리 이름을 자동으로 읽어옴
              const font = new FontFace(fontFamily.family, `url(${fontPath})`, fontDescriptor);
              
              // 폰트 로드
              await font.load();
              
              // document.fonts에 추가
              document.fonts.add(font);
              
              loadedWeights.push(weightInfo);
            } catch (fontError) {
              console.warn(`폰트 로드 실패: ${fontFamily.name} ${weightInfo.name}`, fontError);
              // 로드 실패해도 목록에는 추가 (시스템 폰트로 폴백)
              loadedWeights.push(weightInfo);
            }
          }
          
          if (loadedWeights.length > 0) {
            loadedFamilies.push({
              ...fontFamily,
              weights: loadedWeights
            });
          }
        }
        
        // 기본 폰트는 PretendardJP (이미 fontData에 포함되어 있음)
        
        setFontFamilies(loadedFamilies);
        setIsLoading(false);
      } catch (err) {
        console.error('폰트 로드 오류:', err);
        setError(err instanceof Error ? err.message : '알 수 없는 오류');
        
        // 기본 폰트만 추가 (PretendardJP는 fontData에 포함되어 있음)
        setFontFamilies([
          {
            family: 'PretendardJP',
            name: 'PretendardJP',
            weights: [
              { file: 'PretendardJP-Regular.ttf', name: 'Regular', weight: '400', value: '400' },
              { file: 'PretendardJP-Bold.ttf', name: 'Bold', weight: '700', value: '700' }
            ]
          }
        ]);
        setIsLoading(false);
      }
    };

    loadFonts();
  }, []);

  return { fontFamilies, isLoading, error };
}

